import { useEffect, Suspense, lazy, Component } from 'react'
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom'
import ReactGA from 'react-ga4'
import Navbar from './components/Navbar'
import Footer from './components/Footer'
import ScrollToTop from './components/ScrollToTop'

const Home               = lazy(() => import('./pages/Home'))
const Booking            = lazy(() => import('./pages/Booking'))
const Confirmation       = lazy(() => import('./pages/Confirmation'))
const MemberPortal       = lazy(() => import('./pages/MemberPortal'))
const ResetPassword      = lazy(() => import('./pages/ResetPassword'))
const CancelBooking      = lazy(() => import('./pages/CancelBooking'))
const SignupBenefits     = lazy(() => import('./pages/SignupBenefits'))
const SignupRegister     = lazy(() => import('./pages/SignupRegister'))
const SignupPayment      = lazy(() => import('./pages/SignupPayment'))
const AddressCheckout    = lazy(() => import('./pages/AddressCheckout'))
const MemberDashboard    = lazy(() => import('./pages/MemberDashboard'))
const AdminDashboard     = lazy(() => import('./pages/AdminDashboard'))
const About              = lazy(() => import('./pages/About'))
const Services           = lazy(() => import('./pages/Services'))
const ProfessionalAddress = lazy(() => import('./pages/services/ProfessionalAddress'))
const VirtualAddress     = lazy(() => import('./pages/services/VirtualAddress'))
const PartTimeOffice     = lazy(() => import('./pages/services/PartTimeOffice'))
const FullTimeOffices    = lazy(() => import('./pages/services/FullTimeOffices'))
const ConferenceRooms    = lazy(() => import('./pages/services/ConferenceRooms'))
const MemberPerks        = lazy(() => import('./pages/MemberPerks'))
const Resources          = lazy(() => import('./pages/Resources'))
const ResourcesPayments  = lazy(() => import('./pages/ResourcesPayments'))
const ResourcesFaqs      = lazy(() => import('./pages/ResourcesFaqs'))
const NewsWhosNews       = lazy(() => import('./pages/NewsWhosNews'))
const NewsPost           = lazy(() => import('./pages/NewsPost'))
const ArtGallery         = lazy(() => import('./pages/ArtGallery'))
const Contact            = lazy(() => import('./pages/Contact'))
const Directory          = lazy(() => import('./pages/Directory'))
const PrivacyPolicy      = lazy(() => import('./pages/PrivacyPolicy'))
const TermsOfService     = lazy(() => import('./pages/TermsOfService'))
const DamageWaiver       = lazy(() => import('./pages/DamageWaiver'))
const MailHandlingAgreement = lazy(() => import('./pages/MailHandlingAgreement'))
const MailboxSetup       = lazy(() => import('./pages/MailboxSetup'))
const CardAuthorizationTerms = lazy(() => import('./pages/CardAuthorizationTerms'))
const RulesAndRegulations   = lazy(() => import('./pages/RulesAndRegulations'))
const NotFound           = lazy(() => import('./pages/NotFound'))

// Routes that provide their own full-screen layout — suppress the public shell
const APP_SHELL_ROUTES = ['/admin', '/member-dashboard']

// /news/whos-news was renamed to /news/ecbcnews — redirect the old URL so existing
// bookmarks/links/search results still land on the right page instead of 404ing.
function RedirectToEcbcNewsPost() {
  const { id } = useParams()
  return <Navigate to={`/news/ecbcnews/${id}`} replace />
}

// Catches errors from lazy-loaded route chunks (e.g. a dynamic import that fails after
// a redeploy replaces the chunk it was pointing at) so navigation fails into a visible
// message instead of an unrecoverable blank page.
class RouteErrorBoundary extends Component {
  state = { hasError: false }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error) {
    console.error('Route render error:', error)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 px-6 text-center">
          <p className="text-lg text-gray-700">Something went wrong loading this page.</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700"
          >
            Reload page
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

const GA_MEASUREMENT_ID = 'G-6SFV21KQEY'
const isLocalhost = ['localhost', '127.0.0.1'].includes(window.location.hostname)
let gaInitialized = false

export default function App() {
  const location = useLocation()
  const { pathname } = location
  const isAppShell = APP_SHELL_ROUTES.some(r => pathname.startsWith(r))

  useEffect(() => {
    if (!isLocalhost && !gaInitialized) {
      ReactGA.initialize(GA_MEASUREMENT_ID)
      gaInitialized = true
    }
  }, [])

  useEffect(() => {
    if (gaInitialized) {
      ReactGA.send({ hitType: 'pageview', page: location.pathname + location.search })
    }
  }, [location])

  return (
    <div className={isAppShell ? '' : 'min-h-screen flex flex-col'}>
      <ScrollToTop />
      {!isAppShell && <Navbar />}
      <main className={isAppShell ? '' : 'flex-1'}>
        <RouteErrorBoundary>
        <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/booking" element={<Booking />} />
          <Route path="/confirmation" element={<Confirmation />} />
          <Route path="/members" element={<MemberPortal />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/cancel-booking" element={<CancelBooking />} />
          <Route path="/signup" element={<SignupBenefits />} />
          <Route path="/signup/register" element={<SignupRegister />} />
          <Route path="/signup/payment" element={<SignupPayment />} />
          <Route path="/checkout/address" element={<AddressCheckout />} />
          <Route path="/member-dashboard" element={<MemberDashboard />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/contacts" element={<AdminDashboard />} />
          <Route path="/about" element={<About />} />
          <Route path="/services" element={<Services />} />
          <Route path="/services/professional-address" element={<ProfessionalAddress />} />
          <Route path="/services/virtual-address" element={<VirtualAddress />} />
          <Route path="/services/part-time-office" element={<PartTimeOffice />} />
          <Route path="/services/full-time-offices" element={<FullTimeOffices />} />
          <Route path="/services/conference-rooms" element={<ConferenceRooms />} />
          <Route path="/member-perks" element={<MemberPerks />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/resources/payments" element={<ResourcesPayments />} />
          <Route path="/resources/faqs" element={<ResourcesFaqs />} />
          <Route path="/news/ecbcnews" element={<NewsWhosNews />} />
          <Route path="/news/ecbcnews/:id" element={<NewsPost />} />
          <Route path="/news/whos-news" element={<Navigate to="/news/ecbcnews" replace />} />
          <Route path="/news/whos-news/:id" element={<RedirectToEcbcNewsPost />} />
          <Route path="/news/art-gallery" element={<ArtGallery />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/directory" element={<Directory />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/damage-waiver" element={<DamageWaiver />} />
          <Route path="/mail-handling-agreement" element={<MailHandlingAgreement />} />
          <Route path="/mailbox-setup" element={<MailboxSetup />} />
          <Route path="/card-authorization-terms" element={<CardAuthorizationTerms />} />
          <Route path="/rules-and-regulations" element={<RulesAndRegulations />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        </RouteErrorBoundary>
      </main>
      {!isAppShell && <Footer />}
    </div>
  )
}
